import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { API_ENDPOINTS } from './constants';
import { Header } from './components/ui';
import { tokens } from './styles/tokens';

// Helper functions
function decimalToAmerican(decimal) {
  if (!decimal || decimal === 0) return '';
  if (decimal >= 2) {
    return `+${Math.round((decimal - 1) * 100)}`;
  } else {
    return `-${Math.round(100 / (decimal - 1))}`;
  }
}

function calcPayout(amount, decimalOdds) {
  if (!amount || !decimalOdds) return '';
  return (parseFloat(amount) * decimalOdds).toFixed(2);
}

function calcProfit(amount, decimalOdds) {
  if (!amount || !decimalOdds) return '';
  return ((parseFloat(amount) * decimalOdds) - parseFloat(amount)).toFixed(2);
}

// Team logo mappings
const teamLogoMapping = {
  "New York Yankees": "yankees.png",
  "Boston Red Sox": "redSox.png", 
  "Tampa Bay Rays": "rays.png",
  "Toronto Blue Jays": "blueJays.png",
  "Baltimore Orioles": "orioles.png",
  "Chicago White Sox": "whiteSox.png",
  "Detroit Tigers": "tigers.png",
  "Kansas City Royals": "royals.png",
  "Minnesota Twins": "twins.png",
  "Cleveland Guardians": "indians.png",
  "Houston Astros": "astros.png",
  "Los Angeles Angels": "angels.png",
  "Oakland Athletics": "athletics.png",
  "Seattle Mariners": "mariners.png",
  "Texas Rangers": "rangers.png",
  "Atlanta Braves": "braves.png",
  "New York Mets": "mets.png",
  "Philadelphia Phillies": "phillies.png",
  "Washington Nationals": "nationals.png",
  "Miami Marlins": "marlins.png",
  "Chicago Cubs": "cubs.png",
  "Milwaukee Brewers": "brewers.png",
  "St. Louis Cardinals": "cardinals.png",
  "Pittsburgh Pirates": "pirates.png",
  "Cincinnati Reds": "reds.png",
  "Los Angeles Dodgers": "dodgers.png",
  "San Francisco Giants": "giants.png",
  "San Diego Padres": "padres.png",
  "Arizona Diamondbacks": "diamondbacks.png",
  "Colorado Rockies": "rockies.png"
};

const eplTeamLogoMapping = {
  "Liverpool": "liverpool.png",
  "Bournemouth": "bournemouth.png", 
  "Aston Villa": "astonVilla.png",
  "Newcastle United": "newcastleUnited.png",
  "Fulham": "fulham.png",
  "Tottenham Hotspur": "tottenhamHotspur.png",
  "Burnley": "burnley.png",
  "West Ham United": "westHamUnited.png",
  "Manchester City": "manchesterCity.png",
  "Brighton and Hove Albion": "brighton.png",
  "Wolverhampton Wanderers": "wolves.png", 
  "Sunderland": "sunderlandAfc.png",
  "Arsenal": "arsenal.png",
  "Chelsea": "chelsea.png", 
  "Manchester United": "manchesterUnited.png",
  "Leicester City": "leicesterCity.png",
  "Everton": "everton.png",
  "Crystal Palace": "crystalPalace.png",
  "Southampton": "southampton.png"
};

const getTeamLogo = (teamName, sport = 'MLB') => {
  const mapping = sport === 'MLB' ? teamLogoMapping : eplTeamLogoMapping;
  const logoFile = mapping[teamName];
  const dir = sport === 'MLB' ? 'MLB' : 'Premier_League';
  return logoFile ? `/logos/${dir}/${logoFile}` : null;
};

const getTeamInitials = (teamName) => {
  return teamName.split(' ').map(word => word[0]).join('').slice(0, 3);
};

const getTeamColors = (teamName) => {
  const hash = teamName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const hue = hash % 360;
  return {
    primary: `hsl(${hue}, 70%, 50%)`,
    secondary: `hsl(${hue}, 70%, 40%)`,
    light: `hsl(${hue}, 70%, 60%)`
  };
};

function CurrentBetPage() {
  const { betId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading: authLoading, getAccessToken, logout, userInfo } = useAuth();
  
  const [bet, setBet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Get bet data from navigation state or fetch from API
  useEffect(() => {
    const initializeBet = async () => {
      // First, try to get bet data from navigation state
      if (location.state?.bet) {
        setBet(location.state.bet);
        setLoading(false);
        return;
      }

      // Fallback: Fetch all bets and find the one we need
      try {
        const accessToken = getAccessToken();
        if (!accessToken) {
          setError('Authentication required');
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_ENDPOINTS.USER_API}/bet`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch bet details');
        }

        const allBets = await response.json();
        const foundBet = allBets.find((bet, index) => {
          // Match by ID or by index if no ID
          return bet.id === betId || bet._id === betId || index.toString() === betId;
        });

        if (foundBet) {
          setBet(foundBet);
        } else {
          setError('Bet not found');
        }
      } catch (err) {
        setError('Failed to load bet details');
        console.error('Error fetching bet:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && betId) {
      initializeBet();
    }
  }, [isAuthenticated, betId, getAccessToken, location.state]);

  if (authLoading || loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: tokens.colors.gradients.dark,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: `linear-gradient(135deg, ${tokens.colors.dark[800]}99 0%, ${tokens.colors.dark[900]}99 100%)`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${tokens.colors.neutral[700]}`,
          borderRadius: tokens.borderRadius['2xl'],
          padding: tokens.spacing[8],
          textAlign: 'center'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: `3px solid ${tokens.colors.neutral[800]}`,
            borderTopColor: tokens.colors.accent[400],
            borderRadius: '50%',
            margin: '0 auto',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ 
            color: tokens.colors.neutral[400], 
            marginTop: tokens.spacing[4],
            fontSize: tokens.typography.fontSize.lg
          }}>
            Loading bet details...
          </p>
        </div>
      </div>
    );
  }

  if (error || !bet) {
    return (
      <div style={{
        minHeight: '100vh',
        background: tokens.colors.gradients.dark,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: `linear-gradient(135deg, ${tokens.colors.dark[800]}99 0%, ${tokens.colors.dark[900]}99 100%)`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${tokens.colors.error[800]}`,
          borderRadius: tokens.borderRadius['2xl'],
          padding: tokens.spacing[8],
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '4rem',
            marginBottom: tokens.spacing[4]
          }}>
            ⚠️
          </div>
          <h2 style={{
            color: tokens.colors.error[400],
            fontSize: tokens.typography.fontSize.xl,
            marginBottom: tokens.spacing[4]
          }}>
            {error || 'Bet not found'}
          </h2>
          <button
            onClick={() => navigate('/my-bets')}
            style={{
              background: tokens.colors.gradients.primary,
              border: 'none',
              borderRadius: tokens.borderRadius.lg,
              padding: `${tokens.spacing[3]} ${tokens.spacing[6]}`,
              color: 'white',
              fontWeight: tokens.typography.fontWeight.semibold,
              cursor: 'pointer'
            }}
          >
            Back to My Bets
          </button>
        </div>
      </div>
    );
  }

  // Determine sport type
  const sport = bet.sport?.toLowerCase().includes('soccer') || bet.sport?.toLowerCase().includes('epl') ? 'EPL' : 'MLB';
  const homeColors = getTeamColors(bet.homeTeam);
  const awayColors = getTeamColors(bet.awayTeam);

  // Status color logic
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'won': return tokens.colors.accent[400];
      case 'lost': return tokens.colors.error[400];
      case 'pending': return tokens.colors.warning[400];
      case 'refunded': return tokens.colors.neutral[400];
      default: return tokens.colors.neutral[400];
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'won': return '🎉';
      case 'lost': return '😞';
      case 'pending': return '⏳';
      case 'refunded': return '↩️';
      default: return '❓';
    }
  };

  const menuItems = [
    { label: 'Home', href: '/home' },
    { label: 'Sports', href: '/sports' },
    { label: 'My Bets', href: '/my-bets', active: true }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: tokens.colors.gradients.dark,
      position: 'relative'
    }}>
      <Header
        logo="ELXR Sportsbook"
        user={userInfo ? {
          name: userInfo.given_name || userInfo.name,
          email: userInfo.email
        } : null}
        onLogout={handleLogout}
        menuItems={menuItems}
      />

      {/* Animated Background */}
      <div style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none'
      }}>
        <div style={{
          position: 'absolute',
          top: '15%',
          left: '10%',
          width: '600px',
          height: '600px',
          background: `radial-gradient(circle, ${tokens.colors.accent[900]}15 0%, transparent 70%)`,
          filter: 'blur(80px)',
          animation: 'float 20s ease-in-out infinite'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '20%',
          right: '15%',
          width: '500px',
          height: '500px',
          background: `radial-gradient(circle, ${tokens.colors.primary[900]}12 0%, transparent 70%)`,
          filter: 'blur(80px)',
          animation: 'float 25s ease-in-out infinite reverse'
        }} />
      </div>

      {/* Content */}
      <div style={{
        position: 'relative',
        zIndex: 1,
        maxWidth: '1200px',
        margin: '0 auto',
        padding: `${tokens.spacing[8]} ${tokens.spacing[6]}`
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: tokens.spacing[8]
        }}>
          <h1 style={{
            fontSize: tokens.typography.fontSize['4xl'],
            fontWeight: tokens.typography.fontWeight.extrabold,
            background: tokens.colors.gradients.glow,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: tokens.spacing[3],
            filter: 'drop-shadow(0 0 20px rgba(34, 197, 94, 0.3))'
          }}>
            Bet Details
          </h1>
          <p style={{
            color: tokens.colors.neutral[400],
            fontSize: tokens.typography.fontSize.xl
          }}>
            Comprehensive overview of your wager
          </p>
        </div>

        {/* Bet Details Card */}
        <div style={{
          background: `linear-gradient(135deg, ${tokens.colors.dark[800]}95 0%, ${tokens.colors.dark[900]}95 100%)`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${tokens.colors.neutral[700]}`,
          borderRadius: tokens.borderRadius['2xl'],
          padding: tokens.spacing[8],
          marginBottom: tokens.spacing[8],
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Status Indicator */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${getStatusColor(bet.status)} 0%, ${getStatusColor(bet.status)}66 100%)`
          }} />
          
          {/* Floating Status Badge */}
          <div style={{
            position: 'absolute',
            top: tokens.spacing[6],
            right: tokens.spacing[6],
            background: `${getStatusColor(bet.status)}22`,
            border: `2px solid ${getStatusColor(bet.status)}`,
            borderRadius: tokens.borderRadius.full,
            padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
            display: 'flex',
            alignItems: 'center',
            gap: tokens.spacing[2]
          }}>
            <span style={{ fontSize: tokens.typography.fontSize.lg }}>
              {getStatusIcon(bet.status)}
            </span>
            <span style={{
              color: getStatusColor(bet.status),
              fontSize: tokens.typography.fontSize.sm,
              fontWeight: tokens.typography.fontWeight.bold,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              {bet.status}
            </span>
          </div>

          <h2 style={{
            color: tokens.colors.neutral[0],
            fontSize: tokens.typography.fontSize['2xl'],
            fontWeight: tokens.typography.fontWeight.bold,
            marginBottom: tokens.spacing[6]
          }}>
            💰 Wager Summary
          </h2>

          {/* Bet Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: tokens.spacing[6],
            marginBottom: tokens.spacing[8]
          }}>
            {/* Bet Amount */}
            <div style={{
              background: `${tokens.colors.primary[900]}22`,
              border: `1px solid ${tokens.colors.primary[700]}`,
              borderRadius: tokens.borderRadius.xl,
              padding: tokens.spacing[6],
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: tokens.typography.fontSize['3xl'],
                fontWeight: tokens.typography.fontWeight.bold,
                color: tokens.colors.primary[400],
                marginBottom: tokens.spacing[2]
              }}>
                {bet.amount} XRP
              </div>
              <div style={{
                color: tokens.colors.neutral[400],
                fontSize: tokens.typography.fontSize.sm,
                fontWeight: tokens.typography.fontWeight.medium,
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Bet Amount
              </div>
            </div>

            {/* Odds */}
            <div style={{
              background: `${tokens.colors.secondary[900]}22`,
              border: `1px solid ${tokens.colors.secondary[700]}`,
              borderRadius: tokens.borderRadius.xl,
              padding: tokens.spacing[6],
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: tokens.typography.fontSize['3xl'],
                fontWeight: tokens.typography.fontWeight.bold,
                color: tokens.colors.secondary[400],
                marginBottom: tokens.spacing[2]
              }}>
                {bet.americanOdds || decimalToAmerican(bet.odds)}
              </div>
              <div style={{
                color: tokens.colors.neutral[400],
                fontSize: tokens.typography.fontSize.sm,
                fontWeight: tokens.typography.fontWeight.medium,
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Odds ({bet.odds})
              </div>
            </div>

            {/* Potential Payout */}
            <div style={{
              background: `${tokens.colors.accent[900]}22`,
              border: `1px solid ${tokens.colors.accent[700]}`,
              borderRadius: tokens.borderRadius.xl,
              padding: tokens.spacing[6],
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: tokens.typography.fontSize['3xl'],
                fontWeight: tokens.typography.fontWeight.bold,
                color: tokens.colors.accent[400],
                marginBottom: tokens.spacing[2]
              }}>
                {calcPayout(bet.amount, bet.odds)} XRP
              </div>
              <div style={{
                color: tokens.colors.neutral[400],
                fontSize: tokens.typography.fontSize.sm,
                fontWeight: tokens.typography.fontWeight.medium,
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Potential Payout
              </div>
              <div style={{
                color: tokens.colors.accent[300],
                fontSize: tokens.typography.fontSize.xs,
                marginTop: tokens.spacing[1]
              }}>
                Profit: +{calcProfit(bet.amount, bet.odds)} XRP
              </div>
            </div>
          </div>

          {/* Bet Details Row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: tokens.spacing[4],
            padding: tokens.spacing[6],
            background: `${tokens.colors.dark[700]}44`,
            borderRadius: tokens.borderRadius.xl,
            border: `1px solid ${tokens.colors.neutral[800]}`
          }}>
            <div>
              <div style={{ color: tokens.colors.neutral[500], fontSize: tokens.typography.fontSize.sm, marginBottom: tokens.spacing[1] }}>
                Prediction
              </div>
              <div style={{ color: tokens.colors.neutral[200], fontWeight: tokens.typography.fontWeight.semibold }}>
                {bet.outcome}
              </div>
            </div>
            
            <div>
              <div style={{ color: tokens.colors.neutral[500], fontSize: tokens.typography.fontSize.sm, marginBottom: tokens.spacing[1] }}>
                Bet Type
              </div>
              <div style={{ color: tokens.colors.neutral[200], fontWeight: tokens.typography.fontWeight.semibold }}>
                {bet.betType || 'Moneyline'}
              </div>
            </div>
            
            <div>
              <div style={{ color: tokens.colors.neutral[500], fontSize: tokens.typography.fontSize.sm, marginBottom: tokens.spacing[1] }}>
                Bookmaker
              </div>
              <div style={{ color: tokens.colors.neutral[200], fontWeight: tokens.typography.fontWeight.semibold }}>
                {bet.gameDetails?.bookmaker || 'N/A'}
              </div>
            </div>
            
            <div>
              <div style={{ color: tokens.colors.neutral[500], fontSize: tokens.typography.fontSize.sm, marginBottom: tokens.spacing[1] }}>
                Placed
              </div>
              <div style={{ color: tokens.colors.neutral[200], fontWeight: tokens.typography.fontWeight.semibold }}>
                {new Date(bet.placedAt || bet.createdAt).toLocaleDateString()}
              </div>
            </div>
          </div>

          {/* Transaction Hash */}
          {bet.xrpTransactionHash && (
            <div style={{
              marginTop: tokens.spacing[6],
              padding: tokens.spacing[4],
              background: `${tokens.colors.neutral[800]}44`,
              borderRadius: tokens.borderRadius.lg,
              border: `1px solid ${tokens.colors.neutral[700]}`
            }}>
              <div style={{ color: tokens.colors.neutral[500], fontSize: tokens.typography.fontSize.sm, marginBottom: tokens.spacing[2] }}>
                XRP Transaction Hash
              </div>
              <div style={{
                color: tokens.colors.accent[400],
                fontSize: tokens.typography.fontSize.sm,
                fontFamily: 'monospace',
                wordBreak: 'break-all'
              }}>
                {bet.xrpTransactionHash}
              </div>
            </div>
          )}
        </div>

        {/* Game Details Card */}
        <div style={{
          background: `linear-gradient(135deg, ${tokens.colors.dark[800]}95 0%, ${tokens.colors.dark[900]}95 100%)`,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${tokens.colors.neutral[700]}`,
          borderRadius: tokens.borderRadius['2xl'],
          padding: tokens.spacing[8],
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Sport Indicator */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: sport === 'MLB' ? 
              `linear-gradient(90deg, ${tokens.colors.primary[400]} 0%, ${tokens.colors.primary[600]} 100%)` :
              `linear-gradient(90deg, ${tokens.colors.secondary[400]} 0%, ${tokens.colors.secondary[600]} 100%)`
          }} />

          <h2 style={{
            color: tokens.colors.neutral[0],
            fontSize: tokens.typography.fontSize['2xl'],
            fontWeight: tokens.typography.fontWeight.bold,
            marginBottom: tokens.spacing[6],
            display: 'flex',
            alignItems: 'center',
            gap: tokens.spacing[3]
          }}>
            <span style={{ fontSize: tokens.typography.fontSize['2xl'] }}>
              {sport === 'MLB' ? '⚾' : '⚽'}
            </span>
            Game Information
          </h2>

          {/* Teams Display */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            gap: tokens.spacing[8],
            marginBottom: tokens.spacing[8]
          }}>
            {/* Home Team */}
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: tokens.borderRadius.lg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                marginBottom: tokens.spacing[4],
                fontSize: tokens.typography.fontSize['2xl'],
                fontWeight: tokens.typography.fontWeight.bold,
                color: homeColors.primary
              }}>
                {getTeamLogo(bet.homeTeam, sport) ? (
                  <img
                    src={getTeamLogo(bet.homeTeam, sport)}
                    alt={bet.homeTeam}
                    style={{
                      width: '120px',
                      height: '120px',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div style={{
                  display: getTeamLogo(bet.homeTeam, sport) ? 'none' : 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  background: `linear-gradient(135deg, ${homeColors.primary}33 0%, ${homeColors.secondary}22 100%)`,
                  border: `2px solid ${homeColors.primary}44`,
                  borderRadius: tokens.borderRadius.lg
                }}>
                  {getTeamInitials(bet.homeTeam)}
                </div>
              </div>
              <h3 style={{
                color: tokens.colors.neutral[0],
                fontSize: tokens.typography.fontSize.xl,
                fontWeight: tokens.typography.fontWeight.bold,
                marginBottom: tokens.spacing[2]
              }}>
                {bet.homeTeam}
              </h3>
              <div style={{
                color: tokens.colors.neutral[500],
                fontSize: tokens.typography.fontSize.sm,
                fontWeight: tokens.typography.fontWeight.medium,
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                HOME
              </div>
            </div>

            {/* VS Separator */}
            <div style={{
              textAlign: 'center',
              padding: tokens.spacing[4]
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: tokens.colors.gradients.card,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                marginBottom: tokens.spacing[3],
                border: `2px solid ${tokens.colors.neutral[700]}`
              }}>
                <span style={{
                  color: tokens.colors.neutral[300],
                  fontSize: tokens.typography.fontSize.xl,
                  fontWeight: tokens.typography.fontWeight.bold
                }}>
                  VS
                </span>
              </div>
              <div style={{
                color: tokens.colors.neutral[400],
                fontSize: tokens.typography.fontSize.sm
              }}>
                {new Date(bet.gameTime || bet.gameDate).toLocaleDateString()}
              </div>
            </div>

            {/* Away Team */}
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: tokens.borderRadius.lg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                marginBottom: tokens.spacing[4],
                fontSize: tokens.typography.fontSize['2xl'],
                fontWeight: tokens.typography.fontWeight.bold,
                color: awayColors.primary
              }}>
                {getTeamLogo(bet.awayTeam, sport) ? (
                  <img
                    src={getTeamLogo(bet.awayTeam, sport)}
                    alt={bet.awayTeam}
                    style={{
                      width: '120px',
                      height: '120px',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div style={{
                  display: getTeamLogo(bet.awayTeam, sport) ? 'none' : 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  background: `linear-gradient(135deg, ${awayColors.primary}33 0%, ${awayColors.secondary}22 100%)`,
                  border: `2px solid ${awayColors.primary}44`,
                  borderRadius: tokens.borderRadius.lg
                }}>
                  {getTeamInitials(bet.awayTeam)}
                </div>
              </div>
              <h3 style={{
                color: tokens.colors.neutral[0],
                fontSize: tokens.typography.fontSize.xl,
                fontWeight: tokens.typography.fontWeight.bold,
                marginBottom: tokens.spacing[2]
              }}>
                {bet.awayTeam}
              </h3>
              <div style={{
                color: tokens.colors.neutral[500],
                fontSize: tokens.typography.fontSize.sm,
                fontWeight: tokens.typography.fontWeight.medium,
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                AWAY
              </div>
            </div>
          </div>

          {/* Game Details Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: tokens.spacing[4],
            padding: tokens.spacing[6],
            background: `${tokens.colors.dark[700]}44`,
            borderRadius: tokens.borderRadius.xl,
            border: `1px solid ${tokens.colors.neutral[800]}`
          }}>
            <div>
              <div style={{ color: tokens.colors.neutral[500], fontSize: tokens.typography.fontSize.sm, marginBottom: tokens.spacing[1] }}>
                Sport
              </div>
              <div style={{ color: tokens.colors.neutral[200], fontWeight: tokens.typography.fontWeight.semibold }}>
                {bet.sport || sport}
              </div>
            </div>
            
            <div>
              <div style={{ color: tokens.colors.neutral[500], fontSize: tokens.typography.fontSize.sm, marginBottom: tokens.spacing[1] }}>
                Game ID
              </div>
              <div style={{ 
                color: tokens.colors.neutral[200], 
                fontWeight: tokens.typography.fontWeight.semibold,
                fontFamily: 'monospace',
                fontSize: tokens.typography.fontSize.sm
              }}>
                {bet.gameId}
              </div>
            </div>
            
            <div>
              <div style={{ color: tokens.colors.neutral[500], fontSize: tokens.typography.fontSize.sm, marginBottom: tokens.spacing[1] }}>
                Game Time
              </div>
              <div style={{ color: tokens.colors.neutral[200], fontWeight: tokens.typography.fontWeight.semibold }}>
                {new Date(bet.gameTime || bet.gameDate).toLocaleString()}
              </div>
            </div>
            
            <div>
              <div style={{ color: tokens.colors.neutral[500], fontSize: tokens.typography.fontSize.sm, marginBottom: tokens.spacing[1] }}>
                Commence Time
              </div>
              <div style={{ color: tokens.colors.neutral[200], fontWeight: tokens.typography.fontWeight.semibold }}>
                {bet.gameDetails?.commenceTime ? new Date(bet.gameDetails.commenceTime).toLocaleString() : 'N/A'}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: tokens.spacing[4],
          marginTop: tokens.spacing[8]
        }}>
          <button
            onClick={() => navigate('/my-bets')}
            style={{
              background: tokens.colors.gradients.card,
              border: `1px solid ${tokens.colors.neutral[700]}`,
              borderRadius: tokens.borderRadius.lg,
              padding: `${tokens.spacing[3]} ${tokens.spacing[6]}`,
              color: tokens.colors.neutral[200],
              fontWeight: tokens.typography.fontWeight.semibold,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = tokens.colors.neutral[600];
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = tokens.colors.neutral[700];
              e.target.style.transform = 'translateY(0)';
            }}
          >
            ← Back to My Bets
          </button>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-30px) rotate(1deg); }
          66% { transform: translateY(-20px) rotate(-1deg); }
        }
      `}</style>
    </div>
  );
}

export default CurrentBetPage; 